import { FC, useState } from 'react';

import { XIcon } from '@heroicons/react/outline';

interface ValidPeriodConsentModalProps {
  open: boolean;
  validPeriod?: string;
  expiresAt?: Date;
  onConfirmNow: () => void;
  onConfirmLater: () => void;
  onClose: () => void;
  isSavingLater?: boolean;
}

const ValidPeriodConsentModal: FC<ValidPeriodConsentModalProps> = ({
  open,
  validPeriod,
  expiresAt,
  onConfirmNow,
  onConfirmLater,
  onClose,
  isSavingLater,
}) => {
  const [checked, setChecked] = useState(false);

  if (!open) return null;

  const periodText = validPeriod || '31일';
  const expiryDateText = expiresAt
    ? new Date(expiresAt).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-stone-800">신청 전 확인</h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-stone-400 transition hover:bg-stone-100"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-bold text-amber-900">
              지금 신청하시면 기프티콘이 발송됩니다
            </p>
            <p className="mt-1.5 break-keep text-sm leading-relaxed text-amber-800">
              발송된 기프티콘은{' '}
              <span className="font-bold text-amber-900">
                발송 시점으로부터 {periodText}
              </span>{' '}
              내에 사용하셔야 합니다.
            </p>
          </div>

          <div className="rounded-lg border border-stone-200 bg-stone-50 px-4 py-3">
            <p className="text-sm font-bold text-stone-700">지금 받지 않으셔도 됩니다</p>
            <p className="mt-1.5 break-keep text-sm leading-relaxed text-stone-600">
              {expiryDateText ? (
                <>
                  <span className="font-bold">{expiryDateText}</span>까지 언제든 신청하실
                  수 있으니, 사용할 시점에 맞춰 신청하시는 것도 좋습니다.
                </>
              ) : (
                <>
                  신청 기한 내에 언제든 신청하실 수 있으니, 사용할 시점에 맞춰 신청하시는
                  것도 좋습니다.
                </>
              )}
            </p>
          </div>
        </div>

        <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 px-4 py-3 transition active:bg-stone-50">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-stone-300 text-stone-800 focus:ring-stone-500"
          />
          <span className="text-sm leading-relaxed text-stone-700">
            위 내용을 확인했습니다.
          </span>
        </label>

        <div className="mt-5 flex gap-3">
          <button
            onClick={onConfirmLater}
            disabled={!checked || isSavingLater}
            className={`flex-1 rounded-lg border-2 py-3 text-sm font-bold transition ${
              checked && !isSavingLater
                ? 'border-stone-500 bg-white text-stone-800 active:bg-stone-300'
                : 'border-stone-200 bg-white text-stone-400'
            }`}
          >
            {isSavingLater ? '저장 중...' : '나중에 신청하기'}
          </button>
          <button
            onClick={onConfirmNow}
            disabled={!checked}
            className={`flex-1 rounded-lg py-3 text-sm font-bold transition ${
              checked
                ? 'bg-stone-800 text-white active:bg-stone-900'
                : 'bg-stone-200 text-stone-400'
            }`}
          >
            지금 받기
          </button>
        </div>
      </div>
    </div>
  );
};

export default ValidPeriodConsentModal;
